import React, { useEffect, useContext, useState } from "react";
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  FacebookIcon,
  LinkedinIcon,
  WhatsappIcon,
  WhatsappShareButton,
  TwitterIcon,
} from "react-share";
import { getFunctions, httpsCallable } from "firebase/functions";
import axios from "axios";
import { dataContext, authContext } from "../context/context";
import { useMediaQuery } from "react-responsive";
import { updateLatestActions, generateRandomString } from "../../utils";
import { FaCopy } from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Rocket from "../../assets/stage.gif";
import Phone from "../../assets/phone.png";
import ProfileComplete from "../../assets/teleicon.png";
import Logo from "../../assets/prairiwhite.png";
import { useNavigate } from "react-router-dom";
import Active from "../../assets/active.png";
import {
  StartupProfile,
  PrairiButton,
  Modal,
  TextButton,
  Loading,
} from "@peterfosso/prairi-components";
import ConfettiExplosion from "react-confetti-explosion";
import MatchingIcon from "../../assets/matching.png";
import {
  collection,
  db,
  getDoc,
  doc,
  query,
  where,
  getDocs,
  updateDoc,
  Timestamp,
  addDoc,
} from "../../firebase/firebaseApp";
import { FormControlLabel, Checkbox } from "@material-ui/core";

const Matching = () => {
  const time = Timestamp.fromDate(new Date());
  const { currentUser } = useContext(authContext);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [currentUserData, setCurrentUserData] = useState({});
  const [currentUserId, setCurrentUserId] = useState("");
  const [dataFetched, setDataFetched] = useState(false);
  const [profileModal, setProfileModal] = useState(false);
  const [isExploding, setIsExploding] = useState(false);
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [whatsappPhone, setWhatsappPhone] = useState("");
  const [phoneError, setPhoneError] = useState(false);
  const [whatsappPhoneError, setWhatsappPhoneError] = useState(false);
  const [location, setLocation] = useState("us");
  const [smsConsent, setSmsConsent] = useState(false);
  const [active, setActive] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [smsError, setSmsError] = useState(false);
  const [phoneSaved, setPhoneSaved] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState({
    email: true,
    sms: true,
    whatsapp: false,
  });
  const [preferencesSaved, setPreferencesSaved] = useState(false);
  const [showPreferences, setShowPreferences] = useState(true);
  const [preferencesError, setPreferencesError] = useState(false);

  const getDataFromUser = async (uid) => {
    try {
      const q = query(collection(db, "users"), where("uid", "==", uid));
      const querySnapshot = await getDocs(q);
      let userData;
      let userId;
      querySnapshot.forEach((doc) => {
        userData = doc.data();
        userId = doc.id;
      });

      // Set user data and preferences
      setCurrentUserData(userData);
      setCurrentUserId(userId);
      setIsExploding(!userData.explosion);

      // Set notification preferences if they exist
      if (userData.notificationPreferences) {
        setNotificationPreferences(userData.notificationPreferences);
      }
      if (userData.active) {
        setActive(userData.active);
      }
      if (
        userData.pendingApproval ||
        (!userData.active && !userData.pendingApproval)
      ) {
        setPendingApproval(userData.pendingApproval);
      }

      // Set phone numbers if they exist
      if (userData.phone) {
        setPhone(userData.phone);
        // If no whatsapp phone is set, use the regular phone number
        if (!userData.whatsappPhone) {
          setWhatsappPhone(userData.phone);
        }
      }
      if (userData.whatsappPhone) {
        setWhatsappPhone(userData.whatsappPhone);
      }

      // Return the userData and userId for immediate use
      return { userData, userId };
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  const fetchData = async () => {
    try {
      if (currentUser) {
        const result = await getDataFromUser(currentUser.uid);
        if (result) {
          const { userData, userId } = result;
          setDataFetched(true);
          if (!userData.explosion) {
            const docRef = doc(db, "users", userId);
            await updateDoc(docRef, { explosion: true });
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentUser]);

  // Check approval status and send email if needed
  useEffect(() => {
    const checkApprovalAndSendEmail = async () => {
      if (!dataFetched) return;

      // If user already has approval status, do nothing
      if (currentUserData.active || currentUserData.pendingApproval) return;

      try {
        // Send email
        await sendEmail(currentUserId);

        // Update user document with pendingApproval
        const userRef = doc(db, "users", currentUserId);
        await updateDoc(userRef, { pendingApproval: true });

        // Update local state
        setCurrentUserData((prev) => ({ ...prev, pendingApproval: true }));
      } catch (error) {
        console.error("Error in approval process:", error);
      }
    };

    checkApprovalAndSendEmail();
  }, [dataFetched]);

  useEffect(() => {
    const checkVerificationAndSendEmail = async () => {
      if (!dataFetched) return;

      // If user already has approval status, do nothing
      if (currentUserData.emailVerified) return;

      try {
        // Send email
        await sendEmailToUser(currentUserId);
        await updateDoc(doc(db, "users", currentUserId), {
          emailVerified: true,
        });
      } catch (error) {
        console.error("Error in verification process:", error);
      }
    };

    checkVerificationAndSendEmail();
  }, [dataFetched]);

  const sendEmail = async (docId) => {
    const functions = getFunctions();
    const sendEmailFn = httpsCallable(functions, "sendEmail");
    try {
      const response = await sendEmailFn({
        templateId: 72,
        recipients: [
          {
            name: "Caro",
            email: "caro@prairi.com",
          },
        ],
        params: {
          URL: `https://www.prairi.com/newprofile/${docId}`,
          COMPANY: currentUserData.formData.company.company,
        },
      });
    } catch (error) {
      console.error("Error sending email", error);
    }
  };

  const sendEmailToUser = async (docId) => {
    const functions = getFunctions();
    const sendEmailFn = httpsCallable(functions, "sendEmail");
    try {
      const response = await sendEmailFn({
        templateId: 73,
        recipients: [
          { name: currentUserData.contactName, email: currentUserData.email },
        ],
        params: {
          NAME: currentUserData.contactName.split(" ")[0],
          URL: `https://www.prairi.com/verify/${docId}`,
          EMAIL: currentUserData.email,
        },
      });
    } catch (error) {
      console.error("Error sending email", error);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  useEffect(() => {
    if (currentUserData.notificationPreferences) {
      setShowPreferences(false);
    }
  }, [currentUserData]);

  const { setDataFunc, globalData } = useContext(dataContext);
  const sharedMessage = `Hey, our startup just joined PRAIRI, where you can find the right investors (without even looking for them). Check it out at https://prairi.com`;
  const url = "https://www.prairi.com";
  const facebookShareUrl = `https://www.prairi.com/sharer/sharer.php?u=${encodeURIComponent(
    url
  )}&quote=${sharedMessage}`;

  const data = globalData.formData;
  const allowed =
    data.company.completed &&
    data.industry.completed &&
    data.founders.completed &&
    data.ownership.completed &&
    data.support.completed;

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const response = await axios.get("https://ipapi.co/json/");
        setLocation(response.data.country_code.toLowerCase());
      } catch (error) {
        console.log(error);
      }
    };
    fetchLocation();
  }, []);

  const handlePhoneChange = (value) => {
    setPhone(value);
    setPhoneError(false);
  };

  const handlePhoneBlur = () => {
    if (!phone || phone.length < 8) {
      setPhoneError(true);
    } else {
      setPhoneError(false);
    }
  };

  // const handleWhatsappPhoneChange = (value) => {
  //   setWhatsappPhone(value);
  //   setWhatsappPhoneError(false);
  // };

  // const handleWhatsappPhoneBlur = () => {
  //   if (!whatsappPhone || whatsappPhone.length < 8) {
  //     setWhatsappPhoneError(true);
  //   } else {
  //     setWhatsappPhoneError(false);
  //   }
  // };

  const sendLoginSMS = async (userData, userId) => {
    //Send ONLY ONCE. So we'll store that.
    try {
      const docRef = doc(db, "users", userId);
      await updateDoc(docRef, { loginSMS: true });
      const messagesCollection = collection(db, "messages");
      const dataToStore = {
        to: `+${phone}`,
        body: `Hi ${userData.contactName.split(" ")[0]}!
You're all set! We'll text you when there's an interested investor.`,
      };
      await addDoc(messagesCollection, dataToStore);
    } catch (error) {
      console.log(error);
    }
  };

  const handlePreferenceChange = (preference) => {
    setNotificationPreferences((prev) => ({
      ...prev,
      [preference]: !prev[preference],
    }));
    setPreferencesSaved(false);
    setPreferencesError(false);
  };

  const handlePreferencesSave = async () => {
    // Validar que al menos email o sms esté seleccionado
    if (!notificationPreferences.email && !notificationPreferences.sms) {
      setPreferencesError(true);
      return;
    }
    try {
      const userRef = doc(db, "users", currentUserId);
      const updateData = {
        notificationPreferences: notificationPreferences,
      };

      if (notificationPreferences.sms) {
        if (!phone || phone.length < 8) {
          setPhoneError(true);
          return;
        }
        updateData.phone = phone;
      }
      await updateDoc(userRef, updateData);
      updateLatestActions(
        userRef,
        "Updated notification preferences",
        isMobile
      );
      setPreferencesSaved(true);
      setPreferencesError(false);

      // Send SMS if SMS notifications are enabled and phone is provided
      if (
        notificationPreferences.sms &&
        phone &&
        phone.length >= 8 &&
        !currentUserData.loginSMS
      ) {
        await sendLoginSMS(currentUserData, currentUserId);
      }

      // Start the timer to hide preferences
      setTimeout(() => {
        navigate("/dataprofile/startup");
      }, 3000);
    } catch (error) {
      console.log(error);
    }
  };

  if (!dataFetched) {
    return (
      <div className="shadow-grey w-full h-full rounded-md mt-2 items-center justify-center flex flex-col mx-auto py-2.5 pb-[520px]">
        <Loading />
      </div>
    );
  }

  if (allowed) {
    return (
      <div className="shadow-grey w-full h-full rounded-md mt-2 items-center justify-center flex flex-col mx-auto py-2.5 pb-[520px]">
        <div></div>
        <div className=" text-center  mt-5 ">
          <img
            className="h-24 mb-5 mx-auto items-center flex"
            src={MatchingIcon}
            alt="Matching"
          />
          <div className=" text-center text-2xl font-bold ">
            {"Your profile is now live and visible to investors."}
          </div>
        </div>
        <div className="justify-center items-center flex">
          {dataFetched && currentUserData && isExploding && (
            <ConfettiExplosion />
          )}
        </div>
        <div className="flex flex-col mt-7 items-center text-md">
          <div className="w-full ">
            <p className="text-start flex-col items-center flex  ">
              You'll hear from us when any of them wish to connect.
            </p>
          </div>

          {(active || !showPreferences) && (
            <div className="flex flex-col justify-between w-2/3 mt-4 items-center mx-auto">
              <PrairiButton
                label="View Profile"
                click={() => navigate("/dataprofile/startup")}
                size="md"
                border="thinGreen"
                rounded="soft"
              />
                          <TextButton
              label="Continue editing your profile"
              click={() => navigate("/investorregistration/company")}
              color="silver"
            />
            </div>
          )}
        </div>

        {!active && showPreferences && (
          <div className="mt-12 border-t border-gray-700 pt-8 w-full max-w-xl">
            <h2 className="uppercase text-center w-full text-xl mb-6">
              Notifications
            </h2>
            <p className=" mb-6">
              In addition to email, we recommend SMS so you can hear from
              investors more quickly:
            </p>
            <div className="flex flex-col space-y-4 px-4">
              <div className="flex flex-col">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={notificationPreferences.sms}
                      onChange={() => handlePreferenceChange("sms")}
                      name="sms"
                      style={{ color: "#92d050" }}
                    />
                  }
                  label="SMS notifications"
                  style={{ color: "white" }}
                />
                {notificationPreferences.sms && (
                  <div className="ml-8 mt-2">
                    <div className="flex items-center">
                      <PhoneInput
                        country={location}
                        value={phone}
                        onChange={handlePhoneChange}
                        onBlur={handlePhoneBlur}
                        inputProps={{
                          id: "phone",
                          required: true,
                          name: "phone",
                          autoComplete: "tel",
                        }}
                        countryCodeEditable={false}
                        enableLongNumbers={true}
                        preserveOrder={["onlyCountries", "preferredCountries"]}
                        inputStyle={{
                          color: "black",
                          width: "100%",
                          borderRadius: "0.25rem",
                          borderWidth: "1px",
                          fontSize: "1rem",
                          paddingTop: "0.625rem",
                          paddingBottom: "0.625rem",
                          paddingRight: "1rem",
                          outline: "none",
                        }}
                        dropdownStyle={{ color: "black" }}
                      />
                    </div>
                    {phoneError && (
                      <div className="text-sm text-red-500 mt-2">
                        Provide a valid phone number for SMS notifications
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* <div className="flex flex-col">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notificationPreferences.whatsapp}
                    onChange={() => handlePreferenceChange('whatsapp')}
                    name="whatsapp"
                    style={{ color: "#92d050" }}
                  />
                }
                label="WhatsApp notifications"
                style={{ color: "white" }}
              />
              {notificationPreferences.whatsapp && (
                <div className="ml-8 mt-2">
                  <div className="flex items-center">
                    <PhoneInput
                      country={location}
                      value={whatsappPhone}
                      onChange={handleWhatsappPhoneChange}
                      onBlur={handleWhatsappPhoneBlur}
                      inputProps={{
                        id: "whatsappPhone",
                      }}
                      countryCodeEditable={false}
                      enableLongNumbers={true}
                      inputStyle={{
                        color: "black",
                        width: "100%",
                        borderRadius: "0.25rem",
                        borderWidth: "1px",
                        fontSize: "1rem",
                        paddingTop: "0.625rem",
                        paddingBottom: "0.625rem",
                        paddingRight: "1rem",
                        outline: "none",
                      }}
                      dropdownStyle={{ color: "black" }}
                    />
                  </div>
                  {whatsappPhoneError && (
                    <div className="text-sm text-red-500 mt-2">
                      Provide a valid phone number for WhatsApp notifications
                    </div>
                  )}
                </div>
              )}
            </div> */}
              {preferencesError && (
                <div className="text-sm text-red-500 mt-2">
                  Please select at least Email or SMS notifications
                </div>
              )}
            </div>
            <div className="flex flex-col justify-center items-center mt-6">
              <button
                className="border border-greenPrairi border-[2px] text-white px-4 py-2 rounded-xl"
                onClick={handlePreferencesSave}
              >
                <span className="font-bold">Save Preferences</span>
                <br />
                and View Your Profile
              </button>
              {preferencesSaved && (
                <p className="text-silverPrairi text-xs">Preferences saved!</p>
              )}
            </div>
          </div>
        )}
        {!active && !showPreferences && (
          <div className="flex items-center justify-center mt-4">
            <div className="text-center text-silverPrairi italic text-md mt-4 w-full">
              You can update your notification preferences anytime in your
              settings.
            </div>
          </div>
        )}

        <Modal
          open={profileModal}
          click={() => setProfileModal(false)}
          border="no-border"
          size="auto"
          gray
        >
          <div className="flex flex-col h-[calc(100vh-4rem)] mx-2 ">
            <div className="flex justify-end mb-4 pr-8">
              <TextButton
                label="Close"
                click={() => setProfileModal(false)}
                color="green"
              />
            </div>

            <div className="flex flex-col h-screen mx-2 overflow-y-auto">
              <StartupProfile startup={currentUserData} basic />
            </div>
          </div>
        </Modal>
      </div>
    );
  } else {
    return (
      <div className="shadow-grey w-full rounded-md mt-2 h-full md:max-w-xl mx-auto py-2.5 pb-[300px]">
        <div className="text-center mt-5">
          <img className="h-16 mb-1 mx-auto" src={Logo} alt="Matching" />
          <h1 className="font-bold text-center mb-8 text-base">
            Matching Pending
          </h1>
        </div>

        <div className="flex">
          {!currentUserData.pendingApproval && dataFetched && (
            <div className="ml-5 mr-3">
              <p className="font-bold mb-3">
                Oops! Your profile is incomplete.
              </p>
              <p className="mb-3 ">
                Please complete it so we can match your startup with investors
                from all around the world.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
};

export default Matching;
